// The one code box for two-step sign-in: signing in, switching it on, switching it off.
// A 6-digit code from the authenticator app, or (where allowed) a recovery code.
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Callout } from "@/components/ui/status";
import { cleanCode, codeSchema, type CodeForm, type CodeMode } from "@/lib/twoFactorModel";

export function TwoFactorCodeForm({
  onCode,
  allowRecovery = false,
  submitLabel = "Continue",
  pendingLabel = "Checking…",
  autoFocus = true,
  idPrefix = "tf",
}: {
  /** Resolves when the code worked; throws with the sentence to show when it did not. */
  onCode: (code: string) => Promise<void>;
  allowRecovery?: boolean;
  submitLabel?: string;
  pendingLabel?: string;
  autoFocus?: boolean;
  idPrefix?: string;
}) {
  const [serverError, setServerError] = useState<string | null>(null);
  const { register, handleSubmit, watch, setValue, setFocus, formState: { errors, isSubmitting } } = useForm<CodeForm>({
    resolver: zodResolver(codeSchema),
    defaultValues: { mode: "app", code: "" },
  });
  const mode = watch("mode");
  const id = `${idPrefix}-code`;

  const switchMode = (next: CodeMode) => {
    setValue("mode", next);
    setValue("code", "");
    setServerError(null);
    setTimeout(() => setFocus("code"), 0);
  };

  const submit = async (values: CodeForm) => {
    setServerError(null);
    try {
      await onCode(cleanCode(values.code, values.mode));
    } catch (err) {
      setValue("code", "");
      setServerError(err instanceof Error ? err.message : "That code did not work. Try again.");
      setTimeout(() => setFocus("code"), 0);
    }
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-4" noValidate>
      <div className="space-y-2">
        <Label htmlFor={id}>{mode === "app" ? "Code from your authenticator app" : "Recovery code"}</Label>
        <Input
          id={id}
          autoFocus={autoFocus}
          autoComplete="one-time-code"
          inputMode={mode === "app" ? "numeric" : "text"}
          spellCheck={false}
          placeholder={mode === "app" ? "123456" : "abcd-efgh-2345"}
          maxLength={mode === "app" ? 7 : 20}
          className="font-mono tracking-widest"
          aria-invalid={!!errors.code}
          {...register("code")}
        />
        {errors.code && <p className="text-xs text-destructive">{errors.code.message}</p>}
      </div>

      {serverError && (
        <Callout tone="danger" icon={AlertTriangle}>
          {serverError}
        </Callout>
      )}

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
        {isSubmitting ? pendingLabel : submitLabel}
      </Button>

      {allowRecovery && (
        <button
          type="button"
          className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          onClick={() => switchMode(mode === "app" ? "recovery" : "app")}
        >
          {mode === "app" ? "Lost your phone? Use a recovery code" : "Use a code from your app instead"}
        </button>
      )}
    </form>
  );
}
